import { supabase } from '../lib/supabase';
import { chatService } from './chatService';

interface ProjectMember {
  user_auth_id: string;
  role: 'admin' | 'member' | 'guest';
}

interface ProjectTeamMember {
  auth_id: string | null;
}

export class ProjectChatService {
  /**
   * Cria ou recupera uma sala de chat para um projeto e adiciona os membros do projeto
   */
  static async ensureProjectChatRoom(
    projectId: string,
    organizationId: string,
    projectName: string
  ): Promise<string> {
    try {
      // Verificar se já existe uma sala para o projeto
      const { data: existingRoom } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('project_id', projectId)
        .single();

      let roomId: string;

      if (existingRoom) {
        roomId = existingRoom.id;
      } else {
        // Criar nova sala se não existir
        const { data: newRoom, error } = await supabase
          .from('chat_rooms')
          .insert({
            name: `Chat do Projeto: ${projectName}`,
            organization_id: organizationId,
            project_id: projectId,
            type: 'project'
          })
          .select()
          .single();

        if (error) {
          console.error('Erro ao criar sala de chat:', error);
          throw error;
        }

        roomId = newRoom.id;
      }

      // Buscar membros do projeto
      const { data: projectMembers, error: membersError } = await supabase
        .from('project_team_members')
        .select('id, name, auth_id, user_id, role')
        .eq('project_id', projectId)
        .eq('organization_id', organizationId)
        .eq('status', 'active');

      if (membersError) {
        console.error('Erro ao buscar membros do projeto:', membersError);
        throw membersError;
      }

      // Adicionar membros à sala de chat se ainda não forem membros
      if (projectMembers && projectMembers.length > 0) {
        console.log(`Processando ${projectMembers.length} membros para adicionar ao chat`);
        
        for (const member of projectMembers) {
          let userAuthId = member.auth_id;
          
          // Se não tem auth_id, tentar buscar pelo user_id
          if (!userAuthId && member.user_id) {
            try {
              const { data: userProfile } = await supabase
                .from('user_profile')
                .select('auth_id')
                .eq('id', member.user_id)
                .single();
                
              if (userProfile?.auth_id) {
                userAuthId = userProfile.auth_id;
              }
            } catch (profileError) {
              console.error(`Erro ao buscar perfil para membro ${member.id}:`, profileError);
            }
          }
          
          if (userAuthId) {
            // Verificar se o membro já está na sala
            const { data: existingMember } = await supabase
              .from('chat_room_members')
              .select('id')
              .eq('room_id', roomId)
              .eq('user_auth_id', userAuthId)
              .single();

            if (!existingMember) {
              await chatService.addChatRoomMember({
                room_id: roomId,
                user_auth_id: userAuthId,
                role: member.role || 'member'
              });
              console.log(`Membro ${userAuthId} adicionado ao chat`);
            } else {
              console.log(`Membro ${userAuthId} já está no chat`);
            }
          }
        }
      }

      return roomId;
    } catch (error) {
      console.error('Erro ao garantir sala de chat:', error);
      throw error;
    }
  }

  /**
   * Adiciona membros do projeto ao chat
   */
  static async syncProjectMembers(
    projectId: string,
    chatRoomId: string
  ): Promise<void> {
    try {
      console.log(`Sincronizando membros do projeto ${projectId} com a sala de chat ${chatRoomId}`);
      
      // Buscar membros do projeto
      const { data: projectMembers, error: membersError } = await supabase
        .from('project_team_members')
        .select('id, name, auth_id, user_id, role')
        .eq('project_id', projectId)
        .eq('status', 'active');

      if (membersError) {
        console.error('Erro ao buscar membros do projeto:', membersError);
        throw membersError;
      }

      if (!projectMembers?.length) {
        console.log('Nenhum membro encontrado para sincronizar');
        return;
      }

      console.log(`Encontrados ${projectMembers.length} membros para sincronizar:`, projectMembers);

      // Preparar membros para adicionar ao chat
      const chatMembers: ProjectMember[] = [];
      
      // Processar cada membro do projeto
      for (const member of projectMembers) {
        console.log(`Processando membro do projeto: ID=${member.id}, Nome=${member.name || 'sem nome'}, auth_id=${member.auth_id || 'não definido'}, user_id=${member.user_id || 'não definido'}`);
        
        // Verificar se o membro tem um nome válido, se não, tentar buscar do user_profile
        if ((!member.name || member.name === 'Usuário sem nome') && member.user_id) {
          try {
            console.log(`Buscando nome para o membro com user_id=${member.user_id}`);
            const { data: userProfile } = await supabase
              .from('user_profile')
              .select('name')
              .eq('id', member.user_id)
              .single();
            
            if (userProfile?.name) {
              console.log(`Nome encontrado para user_id=${member.user_id}: ${userProfile.name}`);
              
              // Atualizar o nome do membro no project_team_members
              const { error: updateError } = await supabase
                .from('project_team_members')
                .update({ name: userProfile.name })
                .eq('id', member.id);
                
              if (updateError) {
                console.error(`Erro ao atualizar nome do membro ${member.id}:`, updateError);
              } else {
                console.log(`Nome do membro ${member.id} atualizado para ${userProfile.name}`);
                member.name = userProfile.name;
              }
            }
          } catch (nameError) {
            console.error(`Erro ao buscar nome para membro com user_id=${member.user_id}:`, nameError);
          }
        }
        
        // Verificar se já temos auth_id
        if (member.auth_id) {
          console.log(`Membro ${member.id} já possui auth_id: ${member.auth_id}`);
          chatMembers.push({
            user_auth_id: member.auth_id,
            role: member.role || 'member'
          });
          continue;
        }
        
        // Se não temos auth_id, mas temos user_id, buscar na tabela user_profile
        if (member.user_id) {
          try {
            console.log(`Buscando auth_id para membro com user_id=${member.user_id}`);
            const { data: userProfile } = await supabase
              .from('user_profile')
              .select('auth_id')
              .eq('id', member.user_id)
              .single();
            
            if (userProfile?.auth_id) {
              console.log(`auth_id encontrado para user_id=${member.user_id}: ${userProfile.auth_id}`);
              
              // Atualizar o auth_id do membro no project_team_members
              const { error: updateError } = await supabase
                .from('project_team_members')
                .update({ auth_id: userProfile.auth_id })
                .eq('id', member.id);
                
              if (updateError) {
                console.error(`Erro ao atualizar auth_id do membro ${member.id}:`, updateError);
              } else {
                console.log(`auth_id do membro ${member.id} atualizado para ${userProfile.auth_id}`);
              }
              
              chatMembers.push({
                user_auth_id: userProfile.auth_id,
                role: member.role || 'member'
              });
            } else {
              console.log(`Nenhum auth_id encontrado para user_id=${member.user_id}`);
            }
          } catch (profileError) {
            console.error(`Erro ao buscar perfil para membro ${member.id}:`, profileError);
          }
        } else {
          console.log(`Membro ${member.id} não possui user_id nem auth_id, não pode ser adicionado ao chat`);
        }
      }

      console.log(`${chatMembers.length} membros prontos para adicionar ao chat`);

      // Adicionar cada membro ao chat
      for (const member of chatMembers) {
        try {
          // Verificar se o membro já está na sala
          const { data: existingMember } = await supabase
            .from('chat_room_members')
            .select('id')
            .eq('room_id', chatRoomId)
            .eq('user_auth_id', member.user_auth_id)
            .single();
          
          if (!existingMember) {
            // Adicionar apenas se não estiver já adicionado
            await chatService.addChatRoomMember({
              room_id: chatRoomId,
              user_auth_id: member.user_auth_id,
              role: member.role
            });
            console.log(`Membro ${member.user_auth_id} adicionado ao chat`);
          } else {
            console.log(`Membro ${member.user_auth_id} já está no chat`);
          }
        } catch (error) {
          console.error(`Erro ao adicionar membro ${member.user_auth_id} ao chat:`, error);
        }
      }
      
      console.log('Sincronização de membros concluída');
    } catch (error) {
      console.error('Erro ao sincronizar membros do projeto:', error);
      throw error;
    }
  }

  /**
   * Migra projetos existentes criando salas de chat
   */
  static async migrateExistingProjects(organizationId: string): Promise<void> {
    try {
      // Buscar projetos sem sala de chat
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          organization_id
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .not('id', 'in', (
          supabase
            .from('chat_rooms')
            .select('project_id')
            .not('project_id', 'is', null)
        ));

      if (projectsError) throw projectsError;
      if (!projects?.length) return;

      // Criar salas de chat para cada projeto
      for (const project of projects) {
        try {
          const chatRoomId = await this.ensureProjectChatRoom(
            project.id,
            project.organization_id,
            project.name
          );
          await this.syncProjectMembers(project.id, chatRoomId);
        } catch (error) {
          console.error(`Erro ao migrar projeto ${project.id}:`, error);
        }
      }
    } catch (error) {
      console.error('Erro ao migrar projetos existentes:', error);
      throw error;
    }
  }
} 